/**
 * Shared suggested-edit contract for MCC/CL clause cards and the Editor tab.
 *
 * Preview HTML and the apply target MUST be derived the same way: fold the AI/manual accepted
 * text, diff it against the live visible paragraph (existing revisions preserved), render ops to
 * track-changes HTML. Word.js stays out of this file so Vitest can lock the parity.
 */

import { buildApplyPlan } from './buildApplyPlan';
import { createProtectedRegionSet } from './protectedRegions';
import { opsToTrackChangesHtml } from './opsToTrackChangesHtml';
import type { Op, RevisionContext } from './types';

/** Same debounce MCC/CL Client Request uses before calling previewHybridOps. */
export const HYBRID_SUGGESTED_EDIT_PREVIEW_DEBOUNCE_MS = 350;

/**
 * How long the Editor holds the suggestion back waiting for that preview.
 *
 * The hold is what makes the suggestion render once instead of twice, but
 * `previewHybridOps` is a `Word.run`, and Word can stall for tens of seconds.
 * Past this cap the fallback renders rather than leaving the lawyer on a
 * shimmer with no suggestion — two renders beat none. Comfortably above the
 * ~3s a healthy round trip takes, well under the 24-48s stalls we have seen.
 */
export const HYBRID_SUGGESTED_EDIT_PREVIEW_GATE_MS = 10_000;

/** Whitespace fold used for hybrid preview target and Submit/Replace apply. */
export function foldHybridCleanTarget(text: string): string {
  return (text || '').replace(/\s+/g, ' ').trim();
}

export function looksLikeTrackChangesHtml(html: string): boolean {
  return html.includes('track-change-insertion') || html.includes('track-change-deletion');
}

/**
 * MCC/CL clause card: accepted text of the suggestion XML when a suggestion exists
 * (`extractAcceptedText(currentEditedXml | localAcceptedCrXml)`), else the client-request
 * plain text. An empty suggestion string is still a suggestion — it is not replaced by the CR.
 */
export function mccClauseCardSuggestionTarget(args: {
  suggestionAcceptedText?: string | null;
  clientRequestPlainText?: string | null;
}): string {
  if (args.suggestionAcceptedText != null) {
    return foldHybridCleanTarget(args.suggestionAcceptedText);
  }
  return foldHybridCleanTarget(args.clientRequestPlainText || '');
}

/**
 * Editor Ask Courty: user-edited suggestion if present, else the model `processedText`.
 */
export function editorSuggestionTarget(args: {
  editedText?: string | null;
  processedText?: string | null;
}): string {
  const edited = (args.editedText || '').trim();
  return foldHybridCleanTarget(edited || args.processedText || '');
}

/**
 * Editor / Document Review preview: hybrid ops HTML when ready, else OOXML fallback.
 * MCC clause-card Client Request does not use this — it always shows the AI-vs-CR XML redline.
 */
export function resolveSuggestedEditPreviewHtml(args: {
  hybridEnabled: boolean;
  hybridPreviewHtml: string | null;
  fallbackHtml?: string | null;
}): string | null {
  if (args.hybridEnabled && args.hybridPreviewHtml != null) {
    return args.hybridPreviewHtml;
  }
  return args.fallbackHtml ?? null;
}

export interface SuggestedEditPreview {
  applyTarget: string;
  ops: Op[];
  html: string;
  targetVisible: string;
}

/**
 * Pure stand-in for `previewHybridOps` + the apply target `applyViaHybridEngine` will receive.
 * MCC clause card and Editor must feed this the same (baseline, target, revisions) triple.
 */
export function computeSuggestedEditPreview(args: {
  liveBaselineVisible: string;
  suggestionAcceptedText: string;
  revisionContext?: RevisionContext;
}): SuggestedEditPreview {
  const applyTarget = foldHybridCleanTarget(args.suggestionAcceptedText);
  const revisionContext = args.revisionContext ?? { revisions: [] };
  const plan = buildApplyPlan(
    args.liveBaselineVisible,
    applyTarget,
    createProtectedRegionSet([]),
    revisionContext,
  );
  return {
    applyTarget,
    ops: plan.ops,
    html: opsToTrackChangesHtml(plan.ops, revisionContext),
    targetVisible: plan.targetVisible,
  };
}
